import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaUpload, FaEye, FaDownload, FaTrash, FaFilePdf, FaFileWord, FaTimes, FaCheckCircle, FaClock } from 'react-icons/fa';
import { collection, getDocs, query, where, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import styles from './CVManagementModal.module.css';

const CVManagementModal = ({ show, onHide, employeeId, userData, isInline = false, documentType = 'CV', allowMultiple = false }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [cvFile, setCVFile] = useState(null);
  const [cvFiles, setCVFiles] = useState([]); // Birden fazla dosya için (IATA)
  const [cvUploading, setCVUploading] = useState(false);
  const [myCVs, setMyCVs] = useState([]);
  const [cvLoading, setCVLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // Drag state

  // Belge türüne göre başlıklar
  const titles = {
    'CV-TR': { 
      main: 'Türkçe CV Yönetimi', 
      upload: 'Türkçe CV Yükle', 
      list: 'Türkçe CV Dosyalarım',
      description: 'Türkçe CV\'nizi PDF, DOC veya DOCX formatında yükleyebilirsiniz.',
      empty: 'Henüz Türkçe CV Belgesi Yüklenmedi',
      emptyDesc: 'Türkçe CV belgenizi yüklemek için "{upload}" sekmesine gidin.'
    },
    'CV-EN': { 
      main: 'İngilizce CV Yönetimi', 
      upload: 'İngilizce CV Yükle', 
      list: 'İngilizce CV Dosyalarım',
      description: 'İngilizce CV\'nizi PDF, DOC veya DOCX formatında yükleyebilirsiniz.',
      empty: 'Henüz İngilizce CV Belgesi Yüklenmedi',
      emptyDesc: 'İngilizce CV belgenizi yüklemek için "{upload}" sekmesine gidin.'
    },
    CV: { 
      main: 'CV Yönetimi', 
      upload: 'CV Yükle', 
      list: 'CV Dosyalarım',
      description: 'Güncel CV\'nizi PDF, DOC veya DOCX formatında yükleyebilirsiniz.',
      empty: 'Henüz CV Belgesi Yüklenmedi',
      emptyDesc: 'CV belgenizi yüklemek için "{upload}" sekmesine gidin.'
    },
    GCP: { 
      main: 'GCP Yönetimi', 
      upload: 'GCP Yükle', 
      list: 'GCP Dosyalarım',
      description: 'GCP belgenizi PDF, DOC veya DOCX formatında yükleyebilirsiniz.',
      empty: 'Henüz GCP Belgesi Yüklenmedi',
      emptyDesc: 'GCP belgenizi yüklemek için "{upload}" sekmesine gidin.'
    },
    IATA: { 
      main: 'IATA Yönetimi', 
      upload: 'IATA Yükle', 
      list: 'IATA Dosyalarım',
      description: 'IATA belgelerinizi PDF, DOC veya DOCX formatında yükleyebilirsiniz. Birden fazla dosya yükleyebilirsiniz.',
      empty: 'Henüz IATA Belgesi Yüklenmedi',
      emptyDesc: 'IATA belgenizi yüklemek için "{upload}" sekmesine gidin.'
    }
  };

  const currentTitles = titles[documentType] || titles.CV;

  // Özgeçmişleri getir
  const fetchMyCVs = useCallback(async () => {
    if (!employeeId) return;
    
    setCVLoading(true);
    try {
      const q = query(
        collection(db, 'CVDocuments'),
        where('employeeId', '==', employeeId),
        where('documentType', '==', documentType)
      );
      
      const snapshot = await getDocs(q);
      const cvList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Manuel sıralama - client-side
      cvList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.uploadedAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.uploadedAt || 0);
        return dateB - dateA; // Yeni dosyalar önce
      });
      
      setMyCVs(cvList);
    } catch (error) {
      console.error('Belgeler yüklenemedi:', error);
      toast.error('Belgeler yüklenirken hata oluştu');
    } finally {
      setCVLoading(false);
    }
  }, [employeeId, documentType]);

  useEffect(() => {
    if (show && employeeId) {
      fetchMyCVs();
    }
  }, [show, employeeId, fetchMyCVs]);

  // Drag and Drop Event Handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Sadece uploadBox'tan tamamen çıkıldığında isDragging'i false yap
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    
    if (files.length === 0) return;

    // Dosya türü ve boyut kontrolü
    const validFiles = files.filter(file => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      const isValidType = 
        fileType === 'application/pdf' || 
        fileType === 'application/msword' || 
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileName.endsWith('.pdf') ||
        fileName.endsWith('.doc') ||
        fileName.endsWith('.docx');

      if (!isValidType) {
        toast.error(`⚠️ ${file.name}: Sadece PDF, DOC veya DOCX dosyaları yüklenebilir`);
        return false;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`⚠️ ${file.name}: Dosya boyutu 10MB'dan küçük olmalıdır`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    if (allowMultiple) {
      // IATA için birden fazla dosya
      setCVFiles(validFiles);
    } else {
      // CV/GCP için tek dosya
      if (validFiles.length > 1) {
        toast.warning('⚠️ Sadece bir dosya seçebilirsiniz. İlk dosya seçildi.');
      }
      setCVFile(validFiles[0]);
    }
  };

  // Özgeçmiş yükleme fonksiyonu
  const handleCVUpload = async () => {
    // IATA için birden fazla dosya kontrolü
    const filesToUpload = allowMultiple && cvFiles.length > 0 ? cvFiles : (cvFile ? [cvFile] : []);

    if (filesToUpload.length === 0) {
      toast.error('⚠️ Lütfen bir dosya seçiniz');
      return;
    }

    // CV-TR, CV-EN ve GCP için tek dosya sınırlaması kontrolü
    if (!allowMultiple && myCVs.length > 0) {
      toast.error(`⚠️ ${documentType} için zaten bir dosya mevcut. Yeni dosya yüklemek için önce mevcut dosyayı silmelisiniz.`);
      return;
    }

    // Tüm dosyaları kontrol et
    for (const file of filesToUpload) {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      const isValidType = 
        fileType === 'application/pdf' || 
        fileType === 'application/msword' || 
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileName.endsWith('.pdf') ||
        fileName.endsWith('.doc') ||
        fileName.endsWith('.docx');

      if (!isValidType) {
        toast.error(`⚠️ ${file.name}: Sadece PDF, DOC veya DOCX dosyaları yüklenebilir`);
        return;
      }

      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`⚠️ ${file.name}: Dosya boyutu 10MB'dan küçük olmalıdır`);
        return;
      }
    }

    setCVUploading(true);

    try {
      let uploadedCount = 0;

      // Her dosyayı sırayla yükle
      for (const file of filesToUpload) {
        const timestamp = Date.now() + uploadedCount; // Her dosya için benzersiz timestamp
        const fileExtension = file.name.split('.').pop();
        const storagePath = `cv-documents/${documentType}/${employeeId}-${timestamp}.${fileExtension}`;
        const storageReference = ref(storage, storagePath);

        // Firebase Storage'a yükle
        await uploadBytes(storageReference, file);
        const downloadURL = await getDownloadURL(storageReference);

        // Firestore'a kaydet
        const cvData = {
          employeeId: employeeId,
          employeeName: `${userData.eName || ''} ${userData.eSurname || ''}`.trim(),
          employeeEmail: userData.eMail || '',
          fileName: file.name,
          fileUrl: downloadURL,
          fileType: fileExtension,
          fileSize: file.size,
          documentType: documentType,
          uploadedAt: new Date().toISOString(),
          createdAt: serverTimestamp()
        };

        await addDoc(collection(db, 'CVDocuments'), cvData);
        uploadedCount++;
      }

      toast.success(`✅ ${uploadedCount} adet ${documentType} belgesi başarıyla yüklendi`);
      
      // State'leri temizle
      setCVFile(null);
      setCVFiles([]);
      
      // Input'u temizle
      const fileInputId = isInline ? 'cv-file-input-inline' : 'cv-file-input';
      const fileInput = document.getElementById(fileInputId);
      if (fileInput) fileInput.value = '';
      
      // Listeyi güncelle ve görüntüleme sekmesine geç
      fetchMyCVs();
      setActiveTab('view');
    } catch (error) {
      console.error(`❌ ${documentType} yükleme hatası:`, error);
      toast.error(`❌ ${documentType} yüklenirken bir hata oluştu: ` + error.message);
    } finally {
      setCVUploading(false);
    }
  };

  // Özgeçmiş silme fonksiyonu
  const handleDeleteCV = async (cvId) => {
    if (!window.confirm(`Bu ${documentType} belgesini silmek istediğinize emin misiniz?`)) return;

    try {
      await deleteDoc(doc(db, 'CVDocuments', cvId));
      toast.success(`✅ ${documentType} belgesi silindi`);
      fetchMyCVs();
    } catch (error) {
      console.error(`❌ ${documentType} silme hatası:`, error);
      toast.error(`❌ ${documentType} silinirken hata oluştu`);
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Tarih formatla
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Dosya tipine göre ikon
  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FaFilePdf className={styles.pdfIcon} />;
    return <FaFileWord className={styles.wordIcon} />;
  };

  // Render upload section
  const renderUploadSection = () => (
    <div className={styles.uploadSection}>
      <div className={styles.uploadInfo}>
        <FaCheckCircle className={styles.infoIcon} />
        <div>
          <h4>{currentTitles.upload}</h4>
          <p>{currentTitles.description}</p>
          <ul className={styles.uploadRules}>
            <li>Maksimum dosya boyutu: 10MB</li>
            <li>Desteklenen formatlar: PDF, DOC, DOCX</li>
            {documentType === 'IATA' && <li>Birden fazla dosya yükleyebilirsiniz</li>}
            {!allowMultiple && <li><strong>Sadece 1 dosya yükleyebilirsiniz. Yeni dosya için önce mevcut dosyayı silin.</strong></li>}
            {documentType === 'CV-TR' && (
              <li>
                <a 
                  href="/templates/Bakanlik_Ozgecmis_Formu.docx" 
                  download="Bakanlik_Ozgecmis_Formu.docx"
                  style={{ color: '#667eea', fontWeight: '600', textDecoration: 'underline' }}
                >
                  📄 Bakanlık Özgeçmiş Formu Şablonunu İndir
                </a>
              </li>
            )}
            {documentType === 'CV-EN' && (
              <li>
                <a 
                  href="/templates/TransCelerate_Abbreviated_CV_Template.docx" 
                  download="TransCelerate_Abbreviated_CV_Template.docx"
                  style={{ color: '#667eea', fontWeight: '600', textDecoration: 'underline' }}
                >
                  📄 TransCelerate CV Template Download
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Tek dosya sınırlaması için mevcut dosya uyarısı */}
      {!allowMultiple && myCVs.length > 0 && (
        <div className={styles.uploadWarning}>
          <FaTimes className={styles.warningIcon} />
          <div>
            <strong>Dikkat!</strong> {documentType} için zaten bir dosya yüklü. Yeni dosya yüklemek için önce mevcut dosyayı silmelisiniz.
          </div>
        </div>
      )}

      <div 
        className={`${styles.uploadBox} ${isDragging ? styles.uploadBoxDragging : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <label htmlFor={isInline ? 'cv-file-input-inline' : 'cv-file-input'} className={styles.fileInputLabel}>
          <div className={styles.fileInputContent}>
            <FaUpload className={styles.uploadIcon} />
            <span className={styles.uploadText}>
              {allowMultiple && cvFiles.length > 0 
                ? `${cvFiles.length} dosya seçildi` 
                : cvFile 
                  ? cvFile.name 
                  : 'Dosya Seç veya Sürükle Bırak'}
            </span>
            {!allowMultiple && cvFile && (
              <span className={styles.fileSize}>{formatFileSize(cvFile.size)}</span>
            )}
          </div>
          <input
            id={isInline ? 'cv-file-input-inline' : 'cv-file-input'}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              if (allowMultiple) {
                setCVFiles(Array.from(e.target.files));
              } else {
                setCVFile(e.target.files[0]);
              }
            }}
            className={styles.fileInput}
            multiple={allowMultiple}
          />
        </label>

        {/* Birden fazla dosya seçildiğinde listeyi göster */}
        {allowMultiple && cvFiles.length > 0 && (
          <div className={styles.selectedFilesList}>
            {cvFiles.map((file, index) => (
              <div key={index} className={styles.selectedFileItem}>
                <span className={styles.selectedFileName}>
                  {getFileIcon(file.name.split('.').pop())}
                  {file.name}
                </span>
                <span className={styles.selectedFileSize}>
                  {formatFileSize(file.size)}
                </span>
                <button
                  onClick={() => {
                    const newFiles = cvFiles.filter((_, i) => i !== index);
                    setCVFiles(newFiles);
                    if (newFiles.length === 0) {
                      const fileInput = document.getElementById(isInline ? 'cv-file-input-inline' : 'cv-file-input');
                      if (fileInput) fileInput.value = '';
                    }
                  }}
                  className={styles.removeFileItemBtn}
                  title="Kaldır"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tek dosya seçildiğinde kaldır butonu */}
        {!allowMultiple && cvFile && (
          <div className={styles.fileActions}>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setCVFile(null);
                const fileInput = document.getElementById(isInline ? 'cv-file-input-inline' : 'cv-file-input');
                if (fileInput) fileInput.value = '';
              }}
              className={styles.removeFileBtn}
            >
              <FaTimes /> Kaldır
            </Button>
          </div>
        )}

        {/* Çoklu dosya seçildiğinde kaldır butonu */}
        {allowMultiple && cvFiles.length > 0 && (
          <div className={styles.fileActions}>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setCVFiles([]);
                const fileInput = document.getElementById(isInline ? 'cv-file-input-inline' : 'cv-file-input');
                if (fileInput) fileInput.value = '';
              }}
              className={styles.removeFileBtn}
            >
              <FaTimes /> Tümünü Kaldır
            </Button>
          </div>
        )}
      </div>

      <div className={styles.uploadButtonContainer}>
        <Button
          variant="primary"
          onClick={handleCVUpload}
          disabled={(allowMultiple ? cvFiles.length === 0 : !cvFile) || cvUploading || (!allowMultiple && myCVs.length > 0)}
          className={styles.uploadButton}
        >
          {cvUploading ? (
            <>
              <FaClock className={styles.spinIcon} />
              Yükleniyor...
            </>
          ) : (
            <>
              <FaUpload />
              {currentTitles.upload}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Render view section
  const renderViewSection = () => (
    <div className={styles.viewSection}>
      {cvLoading ? (
        <div className={styles.loading}>
          <FaClock className={styles.spinIcon} />
          <p>Yükleniyor...</p>
        </div>
      ) : myCVs.length === 0 ? (
        <div className={styles.emptyState}>
          <FaFileWord className={styles.emptyIcon} />
          <h4>{currentTitles.empty}</h4>
          <p>{currentTitles.emptyDesc.replace('{upload}', currentTitles.upload)}</p>
          <Button
            variant="primary"
            onClick={() => setActiveTab('upload')}
            className={styles.goToUploadBtn}
          >
            <FaUpload /> {currentTitles.upload}
          </Button>
        </div>
      ) : (
        <div className={styles.cvList}>
          {myCVs.map((cv) => (
            <div key={cv.id} className={styles.cvCard}>
              <div className={styles.cvIcon}>
                {getFileIcon(cv.fileType)}
              </div>
              <div className={styles.cvInfo}>
                <h5 className={styles.cvFileName}>{cv.fileName}</h5>
                <div className={styles.cvMeta}>
                  <span className={styles.cvDate}>
                    <FaClock /> {formatDate(cv.uploadedAt)}
                  </span>
                  <span className={styles.cvSize}>
                    {formatFileSize(cv.fileSize)}
                  </span>
                </div>
              </div>
              <div className={styles.cvActions}>
                <a
                  href={cv.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.actionBtn}
                  title="Görüntüle"
                >
                  <FaEye />
                </a>
                <a
                  href={cv.fileUrl}
                  download={cv.fileName}
                  className={styles.actionBtn}
                  title="İndir"
                >
                  <FaDownload />
                </a>
                <button
                  onClick={() => handleDeleteCV(cv.id)}
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  title="Sil"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return isInline ? (
    // Inline Mode - Modal değil, direkt içerik
    <div className={styles.inlineContainer}>
      <div className={styles.inlineHeader}>
        <div className={styles.inlineTitle}>
          <FaUpload className={styles.titleIcon} />
          {currentTitles.main}
        </div>
      </div>

      <div className={styles.inlineBody}>
        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          <button
            className={`${styles.tabButton} ${activeTab === 'upload' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <FaUpload />
            <span>{currentTitles.upload}</span>
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'view' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('view')}
          >
            <FaEye />
            <span>{currentTitles.list}</span>
            {myCVs.length > 0 && (
              <span className={styles.cvCount}>{myCVs.length}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'upload' && renderUploadSection()}
          {activeTab === 'view' && renderViewSection()}
        </div>
      </div>
    </div>
  ) : (
    // Modal Mode
    <Modal show={show} onHide={onHide} size="lg" centered className={styles.cvModal}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <FaUpload className={styles.titleIcon} />
          {currentTitles.main}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          <button
            className={`${styles.tabButton} ${activeTab === 'upload' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <FaUpload />
            <span>{currentTitles.upload}</span>
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'view' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('view')}
          >
            <FaEye />
            <span>{currentTitles.list}</span>
            {myCVs.length > 0 && (
              <span className={styles.cvCount}>{myCVs.length}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'upload' && renderUploadSection()}
          {activeTab === 'view' && renderViewSection()}
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <Button variant="secondary" onClick={onHide}>
          Kapat
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CVManagementModal;
